import React from 'react';
import { ExternalLink, BookOpen, FileText, Download, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../translations';

/**
 * TechnicalReferences - Página de Referências Técnicas
 * 
 * Substitui o DocumentViewer interno por links para documentos oficiais públicos.
 * DISCLAIMER: HeveaTalent não é afiliado a INCAPER ou CATI.
 */
const TechnicalReferences = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const references = [
    {
      id: 'incaper',
      title: 'Guia Prático do Seringueiro',
      source: 'INCAPER - Instituto Capixaba de Pesquisa',
      year: 2013,
      pages: 24,
      description: 'Guia completo e atualizado sobre técnicas de sangria da seringueira. Inclui: abertura de painel, ângulo de corte (33°), profundidade, consumo de casca, sistemas de sangria, ethrel, fungicida e prevenção de TPD.',
      url: 'https://biblioteca.incaper.es.gov.br/digital/bitstream/item/487/1/Sangria-da-Seringueira-2013-AInfo.pdf',
      color: 'green',
      recommended: true,
      topics: [
        'Abertura de painel (Págs. 9-13)',
        'Ângulo de corte 33° (Pág. 11, 15)',
        'Profundidade 1,5mm (Pág. 16)',
        'Consumo de casca (Pág. 16)',
        'Sistemas d/3, d/4 (Pág. 13)',
        'Ethrel/Estimulante (Págs. 18-19)',
        'Fungicida e EPIs (Págs. 17-18)',
        'TPD/Painel seco (Pág. 20)'
      ]
    },
    {
      id: 'cati',
      title: 'Manual Técnico 72 - Cultura da Seringueira',
      source: 'CATI/SP - Coordenadoria de Assistência Técnica Integral',
      year: 2010,
      pages: 163,
      description: 'Manual técnico completo para o Estado de São Paulo. Aborda desde implantação até colheita, incluindo clones recomendados, zoneamento climático, doenças e pragas.',
      url: 'https://www.cati.sp.gov.br/portal/themes/unify/arquivos/produtos-e-servicos/acervo-tecnico/Manual%20T%C3%A9cnico%2072%20-%20A%20CULTURA%20DA%20SERINGUEIRA%20PARA%20O%20ESTADO%20DE%20S%C3%83O%20PAULO.pdf',
      color: 'purple',
      recommended: true,
      topics: [
        'Implantação do seringal',
        'Clones recomendados para SP',
        'Zoneamento climático',
        'Técnicas de sangria',
        'Doenças e pragas',
        'Controle fitossanitário',
        'Aspectos econômicos'
      ]
    }
  ];

  const colorClasses = {
    green: {
      bg: 'bg-green-50',
      border: 'border-green-200 hover:border-green-400',
      badge: 'bg-green-600',
      icon: 'text-green-600',
      button: 'bg-green-600 hover:bg-green-700'
    },
    purple: {
      bg: 'bg-purple-50',
      border: 'border-purple-200 hover:border-purple-400',
      badge: 'bg-purple-600',
      icon: 'text-purple-600',
      button: 'bg-purple-600 hover:bg-purple-700'
    },
    amber: {
      bg: 'bg-amber-50',
      border: 'border-amber-200 hover:border-amber-400',
      badge: 'bg-amber-600',
      icon: 'text-amber-600',
      button: 'bg-amber-600 hover:bg-amber-700'
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-600 to-green-800 text-white py-8">
        <div className="container mx-auto px-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-green-100 hover:text-white mb-4 transition-colors"
          >
            <ArrowLeft size={20} />
            <span>{t('trBack')}</span>
          </button>
          
          <div className="flex items-center gap-3">
            <BookOpen size={40} />
            <div>
              <h1 className="text-3xl font-bold">{t('trTitle')}</h1>
              <p className="text-green-100 mt-1">
                Documentos oficiais públicos sobre heveicultura e sangria
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="container mx-auto px-4 py-4">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            <strong>ℹ️ Nota:</strong> HeveaTalent não é afiliado a INCAPER ou CATI. 
            Os links abaixo direcionam para os sites oficiais das instituições. 
            Os critérios técnicos utilizados na plataforma são baseados em literatura pública da área.
          </p>
        </div>
      </div>

      {/* Lista de Referências */}
      <div className="container mx-auto px-4 py-6">
        <div className="grid gap-6">
          {references.map(ref => {
            const colors = colorClasses[ref.color];
            
            return (
              <div
                key={ref.id}
                className={`${colors.bg} border-2 ${colors.border} rounded-xl p-6 transition-all shadow-sm hover:shadow-md`}
              >
                <div className="flex flex-col lg:flex-row lg:items-start gap-6">
                  {/* Info Principal */}
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <span className={`${colors.badge} text-white text-xs font-bold px-3 py-1 rounded-full`}>
                        {ref.source.split(' - ')[0]}
                      </span>
                      <span className="text-gray-500 text-sm">{ref.year}</span>
                      {ref.recommended && (
                        <span className="bg-green-100 text-green-700 text-xs font-medium px-2 py-1 rounded">
                          ⭐ Recomendado
                        </span>
                      )}
                      {ref.isOnlineCourse && (
                        <span className="bg-amber-100 text-amber-700 text-xs font-medium px-2 py-1 rounded">
                          🎓 Curso Online
                        </span>
                      )}
                    </div>
                    
                    <h2 className="text-xl font-bold text-gray-800 mb-2">{ref.title}</h2>
                    <p className="text-sm text-gray-600 mb-1">{ref.source}</p>
                    <p className="text-gray-700 mb-4">{ref.description}</p>
                    
                    {/* Tópicos */}
                    <div className="mb-4">
                      <p className="text-sm font-semibold text-gray-700 mb-2">{t('trContentCovered')}</p>
                      <div className="flex flex-wrap gap-2">
                        {ref.topics.map((topic, idx) => (
                          <span
                            key={idx}
                            className="bg-white border border-gray-200 text-gray-600 text-xs px-2 py-1 rounded"
                          >
                            {topic}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                  
                  {/* Botão de Acesso */}
                  <div className="flex flex-col items-center gap-2 lg:min-w-[200px]">
                    <FileText className={colors.icon} size={48} />
                    <p className="text-sm text-gray-500">
                      {typeof ref.pages === 'number' ? `${ref.pages} páginas` : ref.pages}
                    </p>
                    <a
                      href={ref.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`${colors.button} text-white px-6 py-3 rounded-lg font-medium flex items-center gap-2 transition-colors w-full justify-center`}
                    >
                      <ExternalLink size={18} />
                      {ref.isOnlineCourse ? 'Acessar Curso' : 'Abrir Documento'}
                    </a>
                    <p className="text-xs text-gray-400 text-center">
                      Abre em nova aba no site oficial
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Informações Adicionais */}
      <div className="container mx-auto px-4 py-8">
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <h3 className="text-lg font-bold text-gray-800 mb-4">📚 Sobre as Referências</h3>
          <div className="grid md:grid-cols-2 gap-6 text-sm text-gray-600">
            <div>
              <h4 className="font-semibold text-gray-700 mb-2">{t('trWhySources')}</h4>
              <ul className="space-y-1">
                <li>✅ Documentos públicos de órgãos governamentais</li>
                <li>✅ Conteúdo técnico validado por especialistas</li>
                <li>✅ Materiais atualizados (2010-2024)</li>
                <li>✅ Disponíveis gratuitamente nos sites oficiais</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-gray-700 mb-2">{t('trHowToUse')}</h4>
              <ul className="space-y-1">
                <li>📖 Consulte os documentos para entender os critérios</li>
                <li>📏 Use como referência para melhorar sua técnica</li>
                <li>🎯 Os parâmetros (33°, 1.5mm, etc.) são baseados nessas fontes</li>
                <li>💡 Em caso de dúvida, consulte a página indicada</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TechnicalReferences;
